import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import ChatbotWidget from "../AI/ChatbotWidget";
import OperatorNotificationBell from "./OperatorNotificationBell";
import { useAuth } from "../../hooks/useAuth";
import { isPlatformOwner } from "../../utils/jwtUtils";
import brandLogo from "../../assets/logo.png";

const navItems = [
  { label: "Dashboard", to: "/dashboard" },
  { label: "Clients", to: "/clients" },
  { label: "Disputes", to: "/disputes" },
  { label: "Dispute Letters", to: "/dispute-letters" },
  { label: "AI Analysis", to: "/ai-analysis" },
  { label: "Billing", to: "/billing" },
  { label: "Client Billing", to: "/client-billing" },
  { label: "Compliance", to: "/compliance" },
  { label: "Contract Templates", to: "/contracts/templates" },
  { label: "Reports", to: "/reports" },
  { label: "Score tracker", to: "/score-tracker" },
  { label: "Tasks", to: "/tasks" },
  { label: "Affiliates", to: "/affiliates" },
  { label: "Automations", to: "/automations" },
  { label: "Import", to: "/import" },
  { label: "Staff", to: "/staff" },
  { label: "Settings", to: "/settings" },
];

function navItemsForRole(role: string | undefined) {
  const r = (role ?? "Admin").trim().toLowerCase();
  if (r === "support") {
    return navItems.filter((i) => ["/dashboard", "/clients", "/disputes"].includes(i.to));
  }
  if (r === "advisor") {
    return navItems.filter((i) => !["/billing", "/settings", "/staff", "/client-billing"].includes(i.to));
  }
  return navItems;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, token, logout } = useAuth();
  const visibleNav = useMemo(() => navItemsForRole(user?.role), [user?.role]);
  const platformOwner = isPlatformOwner(token);
  const initials = (user?.operatorEmail?.charAt(0) ?? "U").toUpperCase();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-4 py-2 text-sm transition ${
      isActive ? "bg-blue-600/20 text-blue-300" : "text-slate-300 hover:bg-slate-800 hover:text-white"
    }`;

  const closeMobile = () => setMobileNavOpen(false);

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-slate-100">
      <div className="flex min-h-screen">
        {mobileNavOpen && (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={closeMobile}
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-50 w-72 transform border-r border-slate-800 bg-slate-900/95 p-6 shadow-xl transition-transform duration-200 ease-out lg:static lg:z-auto lg:translate-x-0 lg:bg-slate-900/80 lg:shadow-none ${
            mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="flex items-start justify-between gap-2 lg:block">
            <Link to="/dashboard" className="mb-8 block" onClick={closeMobile}>
              <img src={brandLogo} alt="IntelliWarrior" className="h-16 w-full object-contain object-left" />
              <p className="mt-3 text-sm text-slate-300">Operator Portal</p>
            </Link>
            <button
              type="button"
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white lg:hidden"
              onClick={closeMobile}
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="space-y-2">
            {visibleNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} onClick={closeMobile}>
                {item.label}
              </NavLink>
            ))}
            {platformOwner && (
              <>
                <div className="my-2 border-t border-slate-800" aria-hidden />
                <NavLink to="/clients/archived" className={linkClass} onClick={closeMobile}>
                  Archived clients
                </NavLink>
              </>
            )}
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-950/80 px-4 py-3 sm:px-6 lg:px-8 lg:py-4">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                className="rounded-lg border border-slate-700 p-2 text-slate-200 hover:bg-slate-800 lg:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-white sm:text-lg">Operator Dashboard</h2>
                <p className="hidden text-sm text-slate-400 sm:block">Manage clients, disputes, and AI workflows.</p>
              </div>
            </div>

            <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
              <OperatorNotificationBell />
              <div className="hidden text-right sm:block">
                <p className="max-w-[10rem] truncate text-sm font-medium text-slate-200 lg:max-w-xs">
                  {user?.operatorEmail ?? "Operator"}
                </p>
                <p className="text-xs text-slate-400">{user?.role ?? "Admin"}</p>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                {initials}
              </div>
              <button
                onClick={logout}
                className="rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
              >
                Logout
              </button>
            </div>
          </header>

          <main className="min-w-0 max-w-full p-4 sm:p-6 lg:p-8">{children}</main>
        </div>
      </div>
      <ChatbotWidget />
    </div>
  );
};

export default DashboardLayout;
